/**
 * Product gallery for ported pages.
 *
 * Replaces Athens' <media-gallery> + Flickity + product-modal.js — about 340 lines plus a
 * slider library — because the artboard draws no slider: a static square panel, a grid of
 * thumbnails, and a chip naming the colour on show. What follows is the whole of that
 * behaviour.
 *
 * Two-way pairing with the variant picker is the part worth explaining. On this store the
 * padlock colours ARE the product's media: nine shots, one per variant. So picking a thumbnail
 * has to select the matching colour, and picking a colour has to show the matching shot, or the
 * "Shown: Red" chip starts lying. Pairing is by variant id carried on the thumbnail, never by
 * index, so a product whose media and variants do not line up shows the image and leaves the
 * selection alone rather than selecting the wrong colour.
 *
 * It listens for `product:variant-change`, which Athens' VariantSelects publishes on document
 * (assets/scripts.js). That is a read of a public event, not a reach into the picker's guts:
 * the picker keeps its own contract (docs/dc-island-contracts.md) and neither side queries the
 * other's markup.
 */
class DcProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-dc-gallery-main]');
    this.chip = this.querySelector('[data-dc-gallery-chip]');
    this.thumbs = Array.from(this.querySelectorAll('[data-dc-gallery-thumb]'));

    if (!this.main || !this.thumbs.length) return;

    this.onThumbClick = this.onThumbClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);

    this.thumbs.forEach((t) => t.addEventListener('click', this.onThumbClick));
    document.addEventListener('product:variant-change', this.onVariantChange);

    // Swipe on the main panel. On a phone the rail is a row of dots, and a photo you cannot
    // swipe reads as broken. Passive listeners, and only a clearly sideways stroke counts, so a
    // vertical page scroll that starts on the photo is never taken for a swipe.
    this.panel = this.main.parentElement;
    this.panel.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.panel.addEventListener('touchend', this.onTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    this.thumbs.forEach((t) => t.removeEventListener('click', this.onThumbClick));
    document.removeEventListener('product:variant-change', this.onVariantChange);
    if (this.panel) {
      this.panel.removeEventListener('touchstart', this.onTouchStart);
      this.panel.removeEventListener('touchend', this.onTouchEnd);
    }
  }

  onThumbClick(event) {
    this.select(event.currentTarget);
  }

  onTouchStart(event) {
    const t = event.touches[0];
    this.touchStart = event.touches.length === 1 ? { x: t.clientX, y: t.clientY } : null;
  }

  onTouchEnd(event) {
    const start = this.touchStart;
    this.touchStart = null;
    if (!start) return;

    const t = event.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    // Swipe left for the next photo, right for the previous; it stops at either end.
    const current = this.thumbs.findIndex((th) => th.classList.contains('dc-on'));
    const next = (current < 0 ? 0 : current) + (dx < 0 ? 1 : -1);
    if (next >= 0 && next < this.thumbs.length) this.select(this.thumbs[next]);
  }

  /** Show the media a thumbnail stands for, and select its variant if it has one. */
  select(thumb) {
    this.show(thumb);

    // Selecting the colour is what keeps the chip honest. The picker's own radio is clicked
    // rather than its state being written, so every listener it has still fires — including the
    // price, availability and SKU updates that a variant change is supposed to trigger.
    const variantId = thumb.dataset.variantId;
    if (!variantId) return;

    const input = document.querySelector(
      `.product-variants input[data-variant-id="${variantId}"], .product-variants option[data-variant-id="${variantId}"]`,
    );
    if (!input || input.checked || input.selected) return;

    if (input.tagName === 'OPTION') {
      const select = input.closest('select');
      if (select) {
        select.value = input.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      input.click();
    }
  }

  /** The variant changed elsewhere (a swatch, a URL with ?variant=). Follow it. */
  onVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    const thumb = this.thumbs.find((t) => Number(t.dataset.variantId) === Number(variant.id));
    if (thumb) this.show(thumb);
    else if (this.chip) this.chip.textContent = this.chipText(variant.title);
  }

  show(thumb) {
    const full = thumb.dataset.full;
    if (full) {
      // srcset would win over a changed src, so it goes. The thumbnail's `data-full` is already
      // the right width for the panel.
      this.main.removeAttribute('srcset');
      this.main.removeAttribute('sizes');
      this.main.src = full;
    }

    this.thumbs.forEach((t) => {
      const on = t === thumb;
      t.classList.toggle('dc-on', on);
      if (on) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });

    if (this.chip && thumb.dataset.variantTitle) {
      this.chip.textContent = this.chipText(thumb.dataset.variantTitle);
    }
  }

  chipText(title) {
    const prefix = (this.dataset.shownLabel || 'Shown:').trim();
    return `${prefix} ${title}`;
  }
}

if (!customElements.get('dc-product-gallery')) {
  customElements.define('dc-product-gallery', DcProductGallery);
}
